import { InfoPopover, InfoPopoverProps, Stack } from 'component-library';
import { forwardRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianViewBox } from 'recharts/types/util/types';
import { useResizeObserver } from 'usehooks-ts';

import { BenchmarkType, ConservationBenchmark } from '@/api/rest/resources/types/fact';
import { SHARED_CHART_COLORS } from '@/components/Charts/constants';
import { useScreenSize } from '@/hooks/useScreenSize';
import { ReferenceLine } from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/Chart';

type BenchmarkReferenceLineProps = {
  value: number;
  benchmark: BenchmarkType | ConservationBenchmark;
  infoPopoverProps?: InfoPopoverProps | null;
  yAxisDomain?: [number, number];
  labelOverride?: string | null;
};

export const RenderBenchmarkReferenceLine = ({
  value,
  benchmark,
  infoPopoverProps,
  yAxisDomain,
  labelOverride,
}: BenchmarkReferenceLineProps) => {
  const hasYAxisDomain =
    yAxisDomain &&
    Array.isArray(yAxisDomain) &&
    typeof yAxisDomain[0] === 'number' &&
    typeof yAxisDomain[1] === 'number';

  const isBenchmarkOutOfYDomain = hasYAxisDomain && (yAxisDomain[0] > value || yAxisDomain[1] < value);

  if (hasYAxisDomain && isBenchmarkOutOfYDomain) {
    return null;
  }

  return (
    <ReferenceLine
      label={(props) => (
        <BenchmarkLabel
          benchmark={benchmark}
          infoPopoverProps={infoPopoverProps}
          labelOverride={labelOverride}
          {...props}
        />
      )}
      stroke={SHARED_CHART_COLORS.referenceLines.stroke}
      strokeDasharray='4 4'
      strokeWidth={1}
      y={value}
      ifOverflow='extendDomain'
    />
  );
};

type BenchmarkLabelProps = {
  benchmark: BenchmarkType | ConservationBenchmark;
  infoPopoverProps?: InfoPopoverProps | null;
  viewBox: CartesianViewBox;
  labelOverride?: string | null;
};

const BenchmarkLabel = ({ benchmark, infoPopoverProps, viewBox, labelOverride }: BenchmarkLabelProps) => {
  const labelRef = useRef(null);
  const { t } = useTranslation();
  const { width: labelWidth, height } = useResizeObserver({ ref: labelRef });

  const { width, y } = viewBox;

  const xPosition = (Number(width) || 0) - ((labelWidth ?? 0) - 36);
  const yPosition = (Number(y) || 0) - (Number(height) || 0) - 10;

  const defaultLabel = t(`global.benchmark.${benchmark}`);
  const displayLabel = labelOverride || defaultLabel;

  return (
    <BaseLabel label={displayLabel} infoPopoverProps={infoPopoverProps} x={xPosition} y={yPosition} ref={labelRef} />
  );
};

type BaseLabelProps = {
  label: string;
  infoPopoverProps?: InfoPopoverProps | null;
  x: number;
  y: number;
};

const BaseLabel = forwardRef<HTMLDivElement, BaseLabelProps>(({ infoPopoverProps, label, x, y }, ref) => {
  const isSmallScreen = useScreenSize() === 'small';

  return (
    <foreignObject x={x} y={y} width='100%' height='100%' style={{ overflow: 'visible' }}>
      <Stack
        direction='row'
        spacing={2}
        ref={ref}
        className='w-fit self-end rounded pl-1 bg-[rgba(255,_255,_255,_0.6)]'
      >
        <Stack direction='row' spacing={1} className='typography-caption whitespace-nowrap '>
          {label}
          {!isSmallScreen && infoPopoverProps && <InfoPopover {...infoPopoverProps} />}
        </Stack>
      </Stack>
    </foreignObject>
  );
});
BaseLabel.displayName = 'BaseLabel';
